// Morning Brief — the signed-in rep's demo prep cards for today.
//
// Data comes from /api/brief/me?format=json, which is served by the morning_briefs
// project mounted into marker_api. Identity is the verified Google token, so this only
// ever shows the caller their own day; admins can preview another rep via `rep`.
//
// Rendered natively here rather than injecting the standalone page's HTML: that page is
// light-themed and ships its own stylesheet, which would fight this app's dark palette.
// Both surfaces read the same projected payload, so they can't drift on content.
//
// 2026-09-12: instrumented. A view is logged when a brief loads, and every card line,
// suggested question and owed item carries a two-tap vote (useful / not useful, with a
// reason on the latter). This is the whole feedback loop for the proactive-Marker work:
// the question a vote answers is "would you have wanted this before the call?", asked
// in the moment by the person it was written for. Votes go to marker_runtime.db via
// marker_api; the brief project itself still writes nothing.

import { useEffect, useMemo, useState } from 'react'
import {
  fetchBriefReps, fetchMe, fetchMyBrief, fetchMyBriefDone, fetchMyBriefVotes, postBriefDone,
  postBriefView, postBriefVote,
} from '../lib/api'
import type {
  BriefDemo, BriefFollowup, BriefItemKind, BriefRepEntry, BriefResponse, BriefVote,
  BriefVoteReason,
} from '../lib/types'
import { NearbyCustomers } from './NearbyCustomers'

// Follow-ups get the warm accent, first demos the cool one, so the shape of the day
// reads before any of the text does.
function KindBadge({ kind, n }: { kind: BriefDemo['kind']; n: number | null }) {
  const follow = kind === 'follow_up'
  return (
    <span
      className={
        'px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ' +
        (follow
          ? 'bg-marker-warn/15 text-marker-warn'
          : 'bg-marker-accent/15 text-marker-accent')
      }
    >
      {follow ? `Follow-up${n ? ` · #${n}` : ''}` : 'First demo'}
    </span>
  )
}

function Chip({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className="px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider
                 border border-marker-border text-marker-muted whitespace-nowrap"
    >
      {children}
    </span>
  )
}

// ---- voting ------------------------------------------------------------------------

const REASONS: { key: BriefVoteReason; label: string }[] = [
  { key: 'already_knew', label: 'already knew' },
  { key: 'not_important', label: 'not important' },
  { key: 'incorrect', label: 'incorrect' },
  { key: 'wrong_timing', label: 'wrong timing' },
  { key: 'other', label: 'other' },
]

type VoteCtx = {
  date: string
  rep: string | null
  votes: Map<string, BriefVote>
  onVoted: (key: string, v: BriefVote) => void
  // Admin preview of another rep's brief: the controls disappear, so an admin's look
  // is never recorded as that rep's reading or that rep's judgment.
  readOnly: boolean
  // Per-VIEWER close-out of a follow-up line (2026-09-24). Reads and writes only the
  // signed-in user's own state — the AE's page is unaffected by what a PS closes.
  isDone: (account: string | null, sourceDate: string, text: string) => boolean
  setDone: (account: string | null, sourceDate: string, text: string, done: boolean) => void
}

// Mirrors the server's _followup_item_hash inputs: account + the follow-up's OWN demo
// date + text. No brief date, so closing survives tomorrow's brief.
function doneKey(account: string | null, sourceDate: string, text: string) {
  return `${(account || '').toLowerCase()}|${sourceDate}|${text.replace(/\s+/g, ' ').trim()}`
}

function voteKey(kind: BriefItemKind, account: string | null, text: string) {
  return `${kind}|${(account || '').toLowerCase()}|${text.replace(/\s+/g, ' ').trim()}`
}

// Two taps, inline, quiet. The control sits at the end of the line it judges so the
// question "would you have wanted THIS?" is unambiguous. It only ever adds to the page;
// a failed post leaves the line exactly as it was.
function VoteBar({ ctx, kind, account, text }: {
  ctx: VoteCtx; kind: BriefItemKind; account: string | null; text: string
}) {
  const key = voteKey(kind, account, text)
  const existing = ctx.votes.get(key)
  const [pendingNo, setPendingNo] = useState(false)
  const [busy, setBusy] = useState(false)
  if (ctx.readOnly) return null

  async function send(vote: 'useful' | 'not_useful', reason: BriefVoteReason | null) {
    setBusy(true)
    try {
      await postBriefVote({
        brief_date: ctx.date, rep: ctx.rep, account, item_kind: kind, item_text: text, vote, reason,
      })
      ctx.onVoted(key, { account, item_kind: kind, item_text: text, vote, reason })
      setPendingNo(false)
    } catch { /* leave the control as it was; the page must not break on instrumentation */ }
    finally { setBusy(false) }
  }

  if (existing && !pendingNo) {
    const label = existing.vote === 'useful'
      ? 'useful'
      : `not useful${existing.reason ? ` · ${existing.reason.replace('_', ' ')}` : ''}`
    return (
      <span className="ml-2 inline-flex items-center gap-1.5 align-middle font-mono text-[10px] uppercase tracking-wider text-marker-muted">
        <span className={existing.vote === 'useful' ? 'text-marker-accent' : ''}>{label}</span>
        <button
          type="button"
          onClick={() => existing.vote === 'useful' ? setPendingNo(true) : send('useful', null)}
          className="opacity-50 hover:opacity-100 underline decoration-dotted"
          title="Change your vote"
        >change</button>
      </span>
    )
  }

  if (pendingNo) {
    return (
      <span className="ml-2 inline-flex flex-wrap items-center gap-1 align-middle">
        <span className="font-mono text-[10px] uppercase tracking-wider text-marker-muted mr-0.5">why?</span>
        {REASONS.map((r) => (
          <button
            key={r.key} type="button" disabled={busy}
            onClick={() => send('not_useful', r.key)}
            className="px-1.5 py-0.5 rounded border border-marker-border font-mono text-[10px]
                       uppercase tracking-wider text-marker-muted hover:text-marker-text
                       hover:border-marker-muted disabled:opacity-40"
          >{r.label}</button>
        ))}
        <button type="button" onClick={() => setPendingNo(false)}
                className="font-mono text-[10px] text-marker-muted opacity-60 hover:opacity-100 ml-1">cancel</button>
      </span>
    )
  }

  return (
    <span className="ml-2 inline-flex items-center gap-1 align-middle opacity-0 group-hover:opacity-100
                     focus-within:opacity-100 transition-opacity">
      <button
        type="button" disabled={busy} onClick={() => send('useful', null)}
        title="Would you have wanted this before the call? Yes."
        className="px-1.5 py-0.5 rounded border border-marker-border font-mono text-[10px]
                   uppercase tracking-wider text-marker-muted hover:text-marker-accent
                   hover:border-marker-accent disabled:opacity-40"
      >useful</button>
      <button
        type="button" disabled={busy} onClick={() => setPendingNo(true)}
        title="Would you have wanted this before the call? No."
        className="px-1.5 py-0.5 rounded border border-marker-border font-mono text-[10px]
                   uppercase tracking-wider text-marker-muted hover:text-marker-warn
                   hover:border-marker-warn disabled:opacity-40"
      >not useful</button>
    </span>
  )
}

// ---- cards -------------------------------------------------------------------------

function DemoCard({ demo, ctx, onAsk }: {
  demo: BriefDemo; ctx: VoteCtx
  // Open a chat scoped to this demo. Optional so the card still renders anywhere this
  // isn't wired up.
  onAsk?: (demo: BriefDemo) => void
}) {
  const follow = demo.kind === 'follow_up'
  // The AE wants to know which PS is joining, and vice versa — never their own name back.
  const counterpart = demo.your_role.startsWith('AE') ? demo.ps : demo.ae
  // Briefs generated before 2026-09-22 have no opportunity_id, so the button simply does
  // not appear on them rather than appearing and failing.
  const canAsk = !!(onAsk && demo.opportunity_id)

  return (
    <article className="flex bg-marker-panel border border-marker-border rounded-lg overflow-hidden">
      <div
        className={
          'shrink-0 w-24 p-4 border-r border-marker-border border-l-[3px] ' +
          (follow ? 'border-l-marker-warn' : 'border-l-marker-accent')
        }
      >
        <div className="font-mono text-sm text-marker-text tabular-nums whitespace-nowrap">
          {demo.time}
        </div>
      </div>

      <div className="flex-1 min-w-0 p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-marker-text font-semibold text-base leading-snug">{demo.account}</h3>
            <div className="text-xs text-marker-muted mt-0.5 truncate" title={demo.title}>
              {demo.title}
            </div>
          </div>
          {/* An explicit button rather than a click target on the whole card. The card
              already contains the per-line useful/not-useful controls, and nesting those
              inside a clickable region makes every vote a near-miss on opening a chat.
              This is also the only way the affordance is discoverable: a card that happens
              to be clickable teaches nobody that it is. */}
          {canAsk && (
            <button
              onClick={() => onAsk!(demo)}
              title={`Open a chat about ${demo.account}, already loaded with this deal's history`}
              className="shrink-0 text-xs px-2.5 py-1.5 rounded-md border border-marker-accent/40
                         text-marker-accent hover:bg-marker-accent/10 transition-colors
                         focus:outline-none focus:ring-2 focus:ring-marker-accent/50"
            >
              Ask about this demo
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-2.5">
          <KindBadge kind={demo.kind} n={demo.demo_number} />
          <Chip>{demo.stage}</Chip>
          <Chip title="Your role on this demo">You: {demo.your_role}</Chip>
          {counterpart && <Chip>With {counterpart}</Chip>}
        </div>

        <ul className="mt-3 space-y-1.5">
          {demo.card.map((line, i) => {
            // The style read is a guess from one call, so it's set apart visually as
            // well as labelled — it must never look like the stated facts above it.
            const inferred = line.toLowerCase().startsWith('inferred')
            return (
              <li
                key={i}
                className={
                  'group text-sm leading-relaxed pl-4 relative ' +
                  (inferred ? 'text-marker-muted italic' : 'text-marker-text/90')
                }
              >
                <span
                  className={
                    'absolute left-0 top-2 w-1.5 h-1.5 rounded-full ' +
                    (inferred ? 'border border-marker-muted' : 'bg-marker-muted/60')
                  }
                />
                {line}
                <VoteBar ctx={ctx} kind="card_line" account={demo.account} text={line} />
              </li>
            )
          })}
        </ul>

        {/* Suggested questions sit below the facts: the card is what's known, these are
            what to do about it. Absent entirely when the evidence couldn't ground one —
            a generic question here would teach reps to skip the block. */}
        {demo.questions?.length > 0 && (
          <div className="mt-3 rounded-md bg-marker-accent/10 px-3 py-2.5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-marker-accent mb-1.5">
              Questions to ask
            </div>
            <ul className="space-y-1.5">
              {demo.questions.map((q, i) => (
                <li key={i} className="group relative pl-4 text-sm text-marker-text/90 leading-relaxed">
                  <span className="absolute left-0 top-0 text-marker-accent font-semibold text-xs
                                   leading-6">?</span>
                  {q}
                  <VoteBar ctx={ctx} kind="question" account={demo.account} text={q} />
                </li>
              ))}
            </ul>
          </div>
        )}

        <NearbyCustomers n={demo.nearby} account={demo.account} />

        <div className="mt-3 pt-2.5 border-t border-dashed border-marker-border
                        flex flex-wrap gap-x-5 gap-y-1 text-xs text-marker-muted">
          <span>
            <span className="text-marker-text/70">Attending:</span>{' '}
            {demo.attendees.length > 0
              ? demo.attendees.map((a) => `${a.name} (${a.title})`).join(', ')
              : 'not listed on the invite'}
          </span>
          <span>
            <span className="text-marker-text/70">Source:</span> {demo.booked_by}
          </span>
        </div>
      </div>
    </article>
  )
}

// Section 2 — debt from calls already had. Styled quieter than the day's demos above it:
// it's a nudge list, not the thing you're about to walk into.
function FollowupCard({ fu, ctx }: { fu: BriefFollowup; ctx: VoteCtx }) {
  let when = fu.date
  try {
    // Parse as local, not UTC — `new Date('2026-08-28')` is midnight UTC and renders as
    // the 27th for anyone west of Greenwich.
    const [y, m, d] = fu.date.split('-').map(Number)
    when = new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
    })
  } catch { /* leave the ISO date */ }

  return (
    <article className="bg-marker-panel border border-marker-border rounded-lg px-4 py-3">
      <div className="flex items-baseline gap-2.5 flex-wrap">
        <span className="text-marker-text font-semibold text-sm">{fu.account}</span>
        <span className="font-mono text-[11px] text-marker-muted">{when}</span>
      </div>
      <ul className="mt-2 space-y-1">
        {fu.owed.map((line, i) => {
          const closed = ctx.isDone(fu.account, fu.date, line)
          return (
            <li
              key={i}
              className={
                'group text-sm pl-5 relative leading-relaxed ' +
                (closed ? 'text-marker-muted/60 line-through' : 'text-marker-text/90')
              }
            >
              <span
                className={'absolute left-0 top-0 text-xs leading-6 ' +
                  (closed ? 'text-marker-accent' : 'text-marker-warn')}
              >
                {closed ? '✓' : '→'}
              </span>
              {line}
              {!ctx.readOnly && (
                // Struck through and kept, not removed. A PS closing an AE's item is saying
                // "I have chased this", and the line is the evidence of what was chased —
                // deleting it would lose exactly what they wanted to keep visible.
                <button
                  type="button"
                  onClick={() => ctx.setDone(fu.account, fu.date, line, !closed)}
                  title={closed
                    ? 'Re-open this for yourself'
                    : 'Close this out on your page only — it stays open for everyone else'}
                  className="ml-2 align-middle font-mono text-[10px] uppercase tracking-wider
                             px-1.5 py-0.5 rounded border border-marker-border text-marker-muted
                             opacity-0 group-hover:opacity-100 focus:opacity-100
                             hover:text-marker-accent hover:border-marker-accent transition-opacity"
                >
                  {closed ? 'reopen' : 'done'}
                </button>
              )}
              <VoteBar ctx={ctx} kind="owed" account={fu.account} text={line} />
            </li>
          )
        })}
      </ul>
      <div className="mt-2 font-mono text-[10px] text-marker-muted">
        {fu.n_unanswered} unanswered · {fu.n_commitments} commitments
        {fu.call_url && (
          <>
            {' · '}
            <a href={fu.call_url} target="_blank" rel="noopener noreferrer"
               className="text-marker-accent hover:underline">call</a>
          </>
        )}
      </div>
    </article>
  )
}

// Admin-only: the day's roster as clickable names. The backend already gates `?rep=` to
// ADMIN_EMAILS and already reports `viewing_other`; this is only the picker that was
// missing. Only reps with demos that day appear — for everyone else there is nothing to
// show, and the backend would say so.
function RepPicker({ reps, selected, onSelect }: {
  reps: BriefRepEntry[]; selected: string | null; onSelect: (calendar: string | null) => void
}) {
  const btn = (active: boolean) =>
    'px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-wider border transition-colors ' +
    (active
      ? 'border-marker-accent text-marker-accent bg-marker-accent/10'
      : 'border-marker-border text-marker-muted hover:text-marker-text hover:border-marker-muted')
  return (
    <div className="mt-4 flex flex-wrap items-center gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-wider text-marker-muted mr-1">View as</span>
      <button type="button" className={btn(selected === null)} onClick={() => onSelect(null)}>me</button>
      {reps.map((r) => (
        <button
          key={r.calendar || r.rep} type="button"
          className={btn(selected === (r.calendar || r.rep))}
          onClick={() => onSelect(r.calendar || r.rep)}
          title={r.calendar || undefined}
        >
          {r.rep} <span className="opacity-60">·{r.demos}</span>
        </button>
      ))}
    </div>
  )
}

export function MorningBrief({ onAskAboutDemo }: {
  /** Open a chat scoped to one demo. Undefined = the buttons don't render. */
  onAskAboutDemo?: (demo: BriefDemo) => void
} = {}) {
  const [data, setData] = useState<BriefResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [votes, setVotes] = useState<Map<string, BriefVote>>(new Map())
  // Follow-up lines THIS viewer has closed out. Private to them: a PS closing a line
  // leaves the AE's page untouched. Loaded once, not per brief date, because a line
  // closed last week must stay closed on today's page.
  const [doneKeys, setDoneKeys] = useState<Set<string>>(new Set())
  const [showClosed, setShowClosed] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [reps, setReps] = useState<BriefRepEntry[]>([])
  const [selectedRep, setSelectedRep] = useState<string | null>(null)

  // Admin discovery, once. Non-admins never see the picker and never call the roster.
  useEffect(() => {
    let cancelled = false
    fetchMe()
      .then(async (me) => {
        if (cancelled || !me.is_admin) return
        setIsAdmin(true)
        try {
          const r = await fetchBriefReps()
          if (!cancelled) setReps(r.reps)
        } catch { /* roster is a convenience; the page works without it */ }
      })
      .catch(() => { /* not signed in via Google, or /api/me unavailable: no picker */ })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchMyBrief(selectedRep || undefined)
      .then(async (r) => {
        if (cancelled) return
        setData(r)
        const demos = r.demos || []
        const fus = r.followups || []
        // Instrumentation, fire-and-forget, and ONLY for a rep's own brief: an admin
        // previewing someone else must not count as that person having read it.
        if (!r.viewing_other) {
          void postBriefView({
            brief_date: r.date, rep: r.rep ?? null, n_demos: demos.length, n_followups: fus.length,
          })
          try {
            const closed = await fetchMyBriefDone()
            if (!cancelled) {
              setDoneKeys(new Set((closed.done || []).map(
                (d) => doneKey(d.account, d.source_date, d.item_text))))
            }
            const mine = await fetchMyBriefVotes(r.date, r.rep ?? null)
            if (cancelled) return
            const m = new Map<string, BriefVote>()
            for (const v of mine.votes) m.set(voteKey(v.item_kind, v.account, v.item_text), v)
            setVotes(m)
          } catch { /* no stored votes is a normal state */ }
        } else {
          setVotes(new Map())
        }
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [selectedRep])

  const demos = data?.demos || []
  const followups = data?.followups || []

  const ctx = useMemo<VoteCtx>(() => ({
    date: data?.date || '',
    rep: data?.rep ?? null,
    votes,
    onVoted: (key, v) => setVotes((prev) => { const n = new Map(prev); n.set(key, v); return n }),
    readOnly: Boolean(data?.viewing_other),
    isDone: (account, sourceDate, text) => doneKeys.has(doneKey(account, sourceDate, text)),
    setDone: (account, sourceDate, text, done) => {
      const k = doneKey(account, sourceDate, text)
      // Optimistic: the control answers immediately and reverts only if the write fails.
      setDoneKeys((prev) => {
        const n = new Set(prev)
        if (done) n.add(k); else n.delete(k)
        return n
      })
      postBriefDone({ account, source_date: sourceDate, item_text: text, done })
        .catch(() => setDoneKeys((prev) => {
          const n = new Set(prev)
          if (done) n.delete(k); else n.add(k)
          return n
        }))
    },
  }), [data?.date, data?.rep, data?.viewing_other, votes, doneKeys])

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-6">
        <header className="pb-4 border-b border-marker-border">
          <h1 className="text-xl font-semibold text-marker-text">Today's demos</h1>
          <div className="text-xs text-marker-muted mt-1 font-mono">
            {data?.date}
            {data?.rep && ` · ${data.rep}`}
            {data?.roles?.length ? ` · ${data.roles.join(' / ')}` : ''}
            {data?.timezone && ` · times in ${data.timezone}`}
          </div>
        </header>

        {isAdmin && reps.length > 0 && (
          <RepPicker reps={reps} selected={selectedRep} onSelect={setSelectedRep} />
        )}

        {data?.viewing_other && (
          <div className="mt-4 px-3 py-2 rounded bg-marker-accent/10 text-marker-accent text-sm">
            Previewing {data.rep}'s brief — read-only; your view and votes aren't recorded here.
          </div>
        )}

        {loading && (
          <div className="mt-6 space-y-3" aria-busy="true">
            <div className="h-28 rounded-lg bg-marker-panel border border-marker-border" />
            <div className="h-28 rounded-lg bg-marker-panel border border-marker-border" />
          </div>
        )}

        {error && (
          <div className="mt-6 px-4 py-3 rounded bg-marker-bad/15 border border-marker-bad
                          text-marker-bad text-sm">
            <div className="font-semibold">Couldn't load your brief.</div>
            <div className="font-mono text-xs mt-1 break-words">{error}</div>
          </div>
        )}

        {!loading && !error && demos.length === 0 && (
          <div className="mt-6 px-6 py-10 rounded-lg bg-marker-panel border border-marker-border
                          text-center text-marker-muted text-sm">
            No demos on your calendar for this day.
          </div>
        )}

        {!loading && !error && demos.length > 0 && (
          <>
            {!ctx.readOnly && (
              <div className="mt-3 text-xs text-marker-muted">
                Hover a line and tap <span className="font-mono uppercase text-[10px]">useful</span> or{' '}
                <span className="font-mono uppercase text-[10px]">not useful</span> — would you have
                wanted it before the call? That's how we learn what's worth putting here.
              </div>
            )}
            <div className="mt-3 space-y-3">
              {demos.map((d, i) => <DemoCard key={`${d.time}-${d.account}-${i}`} demo={d} ctx={ctx} onAsk={onAskAboutDemo} />)}
            </div>
          </>
        )}

        {!loading && !error && followups.length > 0 && (
          <section className="mt-9 pt-5 border-t border-marker-border">
            {(() => {
              // A follow-up counts as closed only when EVERY line on it is closed. A card
              // with one item left is still open work and stays in the main list.
              const isClosed = (f: BriefFollowup) =>
                f.owed.length > 0 && f.owed.every((l) => ctx.isDone(f.account, f.date, l))
              const open = followups.filter((f) => !isClosed(f))
              const closed = followups.filter(isClosed)
              return (
                <>
                  <h2 className="flex items-baseline gap-2.5 text-marker-text font-semibold text-base">
                    Post-demo follow-ups
                    <span className="font-mono text-[11px] font-normal text-marker-muted">
                      {open.length} to close out
                      {closed.length > 0 && ` · ${closed.length} closed`}
                    </span>
                  </h2>
                  <div className="mt-3 space-y-2">
                    {open.map((f, i) => (
                      <FollowupCard key={`${f.date}-${f.account}-${i}`} fu={f} ctx={ctx} />
                    ))}
                  </div>
                  {closed.length > 0 && (
                    // Closed work is folded away, never deleted. Closing is a private
                    // judgement and a reversible one, so it has to stay reachable.
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => setShowClosed((v) => !v)}
                        className="font-mono text-[11px] text-marker-muted hover:text-marker-text
                                   underline decoration-dotted"
                      >
                        {showClosed ? 'hide' : 'show'} {closed.length} closed
                      </button>
                      {showClosed && (
                        <div className="mt-2 space-y-2 opacity-60">
                          {closed.map((f, i) => (
                            <FollowupCard key={`c-${f.date}-${f.account}-${i}`} fu={f} ctx={ctx} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )
            })()}
          </section>
        )}

        {data?.generated_at && (
          <div className="mt-8 pt-4 border-t border-marker-border text-xs text-marker-muted space-y-1">
            <div>
              Prep cards are generated from Gong call extractions, Salesforce and calendar
              invites. Generated {data.generated_at.replace('T', ' ')}.
            </div>
            <div>
              Marker records that this page was opened and how you rate each line, so we can
              learn what's worth showing. Ratings are seen by Sales Ops only.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
